import type {
  Appointment,
  CarePlan,
  FollowUp,
  Intervention,
  MedicationRelatedProblem,
  MtmAssessment,
  Patient,
  PatientAllergy,
  PatientCondition,
  PatientMedication,
  Pharmacist,
  Service,
  User,
} from "@dosa/types";
import { newId, nowIso } from "./id";

/**
 * In-memory data store used ONLY for local/offline demonstration of the DOSA MVP without a
 * live Cloudflare Worker, D1, or authentik deployment. This file has no bearing on
 * production behavior — apps/worker implements the real, authorized API. See
 * docs/ARCHITECTURE.md ("Local demo mode vs. production").
 *
 * State resets on page reload; it is intentionally not persisted to avoid the appearance of
 * durable storage for health data in a demo context.
 */

export const db = {
  users: [] as User[],
  patients: [] as Patient[],
  pharmacists: [] as Pharmacist[],
  services: [] as Service[],
  patientMedications: [] as PatientMedication[],
  patientConditions: [] as PatientCondition[],
  patientAllergies: [] as PatientAllergy[],
  appointments: [] as Appointment[],
  mtmAssessments: [] as MtmAssessment[],
  mrps: [] as MedicationRelatedProblem[],
  interventions: [] as Intervention[],
  carePlans: [] as CarePlan[],
  followUps: [] as FollowUp[],
};

function seed() {
  const patientUserId = "usr-demo-patient-1";
  const pharmacistUserId = "usr-demo-pharmacist-1";
  const adminUserId = "usr-demo-admin-1";

  db.users.push(
    { id: patientUserId, authentikSub: "demo-sub-patient-1", email: "patient.demo@dosa.local", role: "PATIENT", status: "ACTIVE", preferredLocale: "ar", createdAt: nowIso(), updatedAt: nowIso() },
    { id: pharmacistUserId, authentikSub: "demo-sub-pharmacist-1", email: "pharmacist.demo@dosa.local", role: "PHARMACIST", status: "ACTIVE", preferredLocale: "en", createdAt: nowIso(), updatedAt: nowIso() },
    { id: adminUserId, authentikSub: "demo-sub-admin-1", email: "admin.demo@dosa.local", role: "ADMIN", status: "ACTIVE", preferredLocale: "en", createdAt: nowIso(), updatedAt: nowIso() },
  );

  const patientId = "pat-demo-1";
  db.patients.push({
    id: patientId,
    userId: patientUserId,
    patientCode: "DOSA-P-000001",
    firstName: "Sara",
    lastName: "Al-Qahtani",
    firstNameAr: "سارة",
    lastNameAr: "القحطاني",
    dateOfBirth: "1988-04-12",
    biologicalSex: "FEMALE",
    city: "Riyadh",
    consentAcceptedAt: nowIso(),
    privacyPolicyVersion: "2026-01-01",
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });

  const pharmacistId = "phm-demo-1";
  db.pharmacists.push({
    id: pharmacistId,
    userId: pharmacistUserId,
    firstName: "Omar",
    lastName: "Al-Harbi",
    firstNameAr: "عمر",
    lastNameAr: "الحربي",
    licenseNumber: "SCFHS-DEMO-0001",
    licenseAuthority: "Saudi Commission for Health Specialties",
    yearsOfExperience: 8,
    status: "ACTIVE",
    maxActivePatients: 50,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });

  db.services.push(
    { id: "svc-initial-mtm", name: "Initial Medication Review", nameAr: "مراجعة دوائية أولية", appointmentType: "INITIAL_MTM", durationMinutes: 45, priceMinorUnits: 25000, currency: "SAR", isActive: true, createdAt: nowIso(), updatedAt: nowIso() },
    { id: "svc-follow-up", name: "Follow-up Review", nameAr: "مراجعة متابعة", appointmentType: "FOLLOW_UP", durationMinutes: 20, priceMinorUnits: 12000, currency: "SAR", isActive: true, createdAt: nowIso(), updatedAt: nowIso() },
    { id: "svc-polypharmacy", name: "Polypharmacy Review", nameAr: "مراجعة تعدد الأدوية", appointmentType: "POLYPHARMACY_REVIEW", durationMinutes: 45, priceMinorUnits: 30000, currency: "SAR", isActive: true, createdAt: nowIso(), updatedAt: nowIso() },
  );

  db.patientAllergies.push({
    id: newId("pal"),
    patientId,
    freeTextAllergen: "Penicillin",
    reaction: "Rash",
    severity: "MODERATE",
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });

  db.patientConditions.push(
    { id: newId("pcn"), patientId, freeTextName: "Type 2 Diabetes", freeTextNameAr: "السكري من النوع الثاني", status: "CHRONIC_MANAGED", createdAt: nowIso(), updatedAt: nowIso() },
    { id: newId("pcn"), patientId, freeTextName: "Hypertension", freeTextNameAr: "ارتفاع ضغط الدم", status: "ACTIVE", createdAt: nowIso(), updatedAt: nowIso() },
  );

  db.patientMedications.push(
    { id: newId("pmd"), patientId, freeTextName: "Metformin", freeTextNameAr: "ميتفورمين", category: "PRESCRIPTION", dose: "500 mg", route: "ORAL", frequency: "TWICE_DAILY", status: "ACTIVE", source: "PATIENT_REPORTED", createdAt: nowIso(), updatedAt: nowIso() },
    { id: newId("pmd"), patientId, freeTextName: "Amlodipine", freeTextNameAr: "أملوديبين", category: "PRESCRIPTION", dose: "5 mg", route: "ORAL", frequency: "ONCE_DAILY", status: "ACTIVE", source: "PATIENT_REPORTED", createdAt: nowIso(), updatedAt: nowIso() },
    { id: newId("pmd"), patientId, freeTextName: "Vitamin D3", freeTextNameAr: "فيتامين د3", category: "SUPPLEMENT", dose: "1000 IU", route: "ORAL", frequency: "ONCE_DAILY", status: "ACTIVE", source: "PATIENT_REPORTED", createdAt: nowIso(), updatedAt: nowIso() },
  );
}

seed();

export function resetMockDb() {
  db.users.length = 0;
  db.patients.length = 0;
  db.pharmacists.length = 0;
  db.services.length = 0;
  db.patientMedications.length = 0;
  db.patientConditions.length = 0;
  db.patientAllergies.length = 0;
  db.appointments.length = 0;
  db.mtmAssessments.length = 0;
  db.mrps.length = 0;
  db.interventions.length = 0;
  db.carePlans.length = 0;
  db.followUps.length = 0;
  seed();
}
